package com.karyadina.ui.screens

import androidx.compose.animation.*

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.outlined.OpenInNew
import androidx.compose.material.icons.automirrored.outlined.TrendingDown
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier

import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.ui.draw.alpha

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.window.Dialog

import com.karyadina.model.Unit as StudyUnit
import com.karyadina.service.*
import com.karyadina.util.rememberImageBitmapFromUri
import androidx.compose.ui.layout.ContentScale
import com.karyadina.model.CropRect
import com.karyadina.model.ImageItem
import com.karyadina.model.ImageResizeConfig
import com.karyadina.model.ImageResizeResult
import com.karyadina.model.ResizePreset

import kotlinx.coroutines.launch
import com.karyadina.ui.components.StudyFloatingActionButton
import com.karyadina.util.rememberCameraPermissionLauncher
import org.jetbrains.compose.resources.stringResource
import com.karyadina.ui.theme.StudyStrings
import org.koin.compose.koinInject
import com.karyadina.util.getLocalActivity



@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ImageResizerScreen(
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val scope = rememberCoroutineScope()

    val fileService = remember { FileServiceImpl() }
    val resizerService = remember { ImageResizerServiceImpl() }
    
    var selectedImage by remember { mutableStateOf<ImageItem?>(null) }
    var selectedPreset by remember { mutableStateOf(ResizePreset.CUSTOM) }
    var resizeConfig by remember { mutableStateOf(ImageResizeConfig()) }
    var showPresetDialog by remember { mutableStateOf(false) }
    var showCropDialog by remember { mutableStateOf(false) }
    var isResizing by remember { mutableStateOf(false) }
    var result by remember { mutableStateOf<ImageResizeResult?>(null) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var successMessage by remember { mutableStateOf<String?>(null) }
    
    // Pre-load strings for coroutines
    val strErrorStart = stringResource(StudyStrings.ResizeErrorStart)
    val strErrorPick = stringResource(StudyStrings.ResizeErrorPick)
    val strErrorCamera = stringResource(StudyStrings.ResizeErrorCamera)
    val strErrorPermission = stringResource(StudyStrings.ResizeErrorPermission)
    val strSuccessMessage = stringResource(StudyStrings.ResizeSuccessMessage)
    
    // Camera Permission Launcher
    val cameraPermissionLauncher = rememberCameraPermissionLauncher { isGranted ->
        if (isGranted) {
            scope.launch {
                try {
                    val image = fileService.takePhoto()
                    if (image != null) {
                        selectedImage = image
                        resizeConfig = resizeConfig.copy(cropRect = null)
                        result = null
                        errorMessage = null
                        successMessage = null
                    }

                } catch (e: Exception) {
                    errorMessage = "$strErrorCamera ${e.message ?: ""}"
                }
            }
        } else {
            errorMessage = strErrorPermission
        }
    }

    
    // Store the last valid crop rect to allow toggling it back on
    var savedCropRect by remember { mutableStateOf<CropRect?>(null) }
    
    // Calculate current step
    val currentStep = when {
        result != null && result?.success == true -> 4 // Done
        selectedImage != null -> 2 // Configure settings
        else -> 1 // Select image
    }
    
    // Animated success banner
    LaunchedEffect(successMessage) {
        if (successMessage != null) {
            kotlinx.coroutines.delay(3000)
            successMessage = null
        }
    }
    
    Scaffold(
        topBar = {
            TopAppBar(
                title = { 
                    Column {
                        Text(stringResource(StudyStrings.ResizeToolTitle), style = MaterialTheme.typography.titleLarge)
                        Text(
                            stringResource(StudyStrings.ResizeToolSubtitle),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, stringResource(StudyStrings.Back))
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.surface,
                    titleContentColor = MaterialTheme.colorScheme.onSurface
                )
            )
        },
        floatingActionButton = {
            AnimatedVisibility(
                visible = selectedImage != null && !isResizing,
                enter = scaleIn() + fadeIn(),
                exit = scaleOut() + fadeOut()
            ) {
                StudyFloatingActionButton(
                    onClick = {
                        isResizing = true
                        errorMessage = null
                        scope.launch {
                            try {
                                val imageToResize = selectedImage!!
                                val resizeResult = resizerService.resizeImage(imageToResize, resizeConfig)
                                result = resizeResult
                                
                                if (resizeResult.success) {
                                    successMessage = strSuccessMessage
                                } else {
                                    errorMessage = resizeResult.error
                                }
                            } catch (e: Exception) {
                                errorMessage = "$strErrorStart ${e.message ?: ""}"
                            } finally {
                                isResizing = false
                            }
                        }
                    },
                    icon = Icons.Filled.AutoFixHigh,
                    contentDescription = "Resize"
                )
            }
        }
    ) { paddingValues ->
        com.karyadina.ui.components.ResponsiveContent {
            Box(modifier = modifier.fillMaxSize()) {
                Column(
                    modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues)
            ) {
                // Success/Error banner
                AnimatedVisibility(
                    visible = successMessage != null || errorMessage != null,
                    enter = slideInVertically() + expandVertically() + fadeIn(),
                    exit = slideOutVertically() + shrinkVertically() + fadeOut()
                ) {
                    Card(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp, vertical = 8.dp),
                        colors = CardDefaults.cardColors(
                            containerColor = if (successMessage != null) 
                                MaterialTheme.colorScheme.primaryContainer
                            else MaterialTheme.colorScheme.errorContainer
                        )
                    ) {
                        Row(
                            modifier = Modifier.padding(16.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                if (successMessage != null) Icons.Filled.CheckCircle else Icons.Filled.Error,
                                contentDescription = null,
                                tint = if (successMessage != null)
                                    MaterialTheme.colorScheme.onPrimaryContainer
                                else MaterialTheme.colorScheme.onErrorContainer
                            )
                            Spacer(Modifier.width(12.dp))
                            Text(
                                successMessage ?: errorMessage ?: "",
                                style = MaterialTheme.typography.bodyMedium,
                                color = if (successMessage != null)
                                    MaterialTheme.colorScheme.onPrimaryContainer
                                else MaterialTheme.colorScheme.onErrorContainer
                            )
                        }
                    }
                }
                
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .verticalScroll(rememberScrollState())
                        .padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(20.dp)
                ) {
                    // Progress indicator
                    LinearProgressIndicator(
                        progress = {currentStep / 4f},
                        modifier = Modifier.fillMaxWidth(),
                        color = MaterialTheme.colorScheme.primary,
                        trackColor = MaterialTheme.colorScheme.surfaceVariant
                    )
                    
                    Spacer(modifier = Modifier.height(8.dp))
                    
                    // STEP 1: Select Image
                    StepCard(
                        stepNumber = 1,
                        title = stringResource(StudyStrings.ResizeStep1Title),
                        subtitle = stringResource(StudyStrings.ResizeStep1Subtitle),
                        isCompleted = selectedImage != null,
                        isActive = selectedImage == null,
                        icon = Icons.Filled.AddPhotoAlternate
                    ) {
                        if (selectedImage == null) {
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                FilledTonalButton(
                                    onClick = {
                                        if (!isResizing) {
                                            scope.launch {
                                                try {
                                                    val images = fileService.pickImages()
                                                    if (images.isNotEmpty()) {
                                                        selectedImage = images.first()
                                                        resizeConfig = resizeConfig.copy(cropRect = null)
                                                        result = null
                                                        errorMessage = null
                                                        successMessage = null
                                                    }
                                                } catch (e: Exception) {
                                                    errorMessage = strErrorPick
                                                }
                                            }
                                        }
                                    },
                                    modifier = Modifier.weight(1f)
                                ) {
                                    Icon(Icons.Filled.Upload, null)
                                    Spacer(Modifier.width(8.dp))
                                    Text(stringResource(StudyStrings.ResizeBtnGallery))
                                }
                                
                                FilledTonalButton(
                                    onClick = {
                                        if (!isResizing) {
                                            cameraPermissionLauncher.launch()
                                        }
                                    },
                                    modifier = Modifier.weight(1f)
                                ) {
                                    Icon(Icons.Filled.CameraAlt, null)
                                    Spacer(Modifier.width(8.dp))
                                    Text(stringResource(StudyStrings.ResizeBtnCamera))
                                }
                            }
                        } else {
                            // Show selected image info
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(12.dp),
                                horizontalArrangement = Arrangement.spacedBy(12.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Surface(
                                    modifier = Modifier.size(56.dp),
                                    shape = MaterialTheme.shapes.medium,
                                    color = MaterialTheme.colorScheme.surfaceVariant
                                ) {
                                    val bitmap = rememberImageBitmapFromUri(selectedImage!!.uri)
                                    if (bitmap != null) {
                                        Image(
                                            bitmap = bitmap,
                                            contentDescription = null,
                                            contentScale = ContentScale.Crop,
                                            modifier = Modifier.fillMaxSize()
                                        )
                                    } else {
                                        Box(contentAlignment = Alignment.Center) {
                                            Icon(
                                                Icons.Filled.Image,
                                                contentDescription = null,
                                                tint = MaterialTheme.colorScheme.onSurfaceVariant
                                            )
                                        }
                                    }
                                }
                                
                                Column(modifier = Modifier.weight(1f)) {
                                    Text(
                                        selectedImage!!.name,
                                        style = MaterialTheme.typography.bodyLarge,
                                        fontWeight = FontWeight.Medium
                                    )
                                    Text(
                                        "${(selectedImage!!.sizeInMB * 100).toInt() / 100.0} MB • ${selectedImage!!.width}×${selectedImage!!.height}",
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                                
                                IconButton(onClick = {
                                    showCropDialog = true
                                }) {
                                    Icon(Icons.Filled.Crop, stringResource(StudyStrings.ResizeActionCrop))
                                }
                                
                                IconButton(onClick = {
                                    selectedImage = null
                                    resizeConfig = resizeConfig.copy(cropRect = null)
                                    result = null
                                }) {
                                    Icon(Icons.Filled.Close, stringResource(StudyStrings.ResizeActionRemove))
                                }
                            }
                        }
                    }
                    
                    // STEP 2: Choose Preset or Custom Settings
                    AnimatedVisibility(visible = selectedImage != null) {
                        StepCard(
                            stepNumber = 2,
                            title = stringResource(StudyStrings.ResizeStep2Title),
                            subtitle = stringResource(StudyStrings.ResizeStep2Subtitle),
                            isCompleted = result?.success == true,
                            isActive = selectedImage != null && result == null,
                            icon = Icons.Filled.Settings
                        ) {
                            // Show "Edit Settings" button if already resized
                            if (result?.success == true) {
                                OutlinedButton(
                                    onClick = {
                                        result = null // Reset to allow editing
                                    },
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    Icon(Icons.Outlined.Edit, null)
                                    Spacer(Modifier.width(8.dp))
                                    Text(stringResource(StudyStrings.ResizeBtnEditSettings))
                                }
                                return@StepCard
                            }
                            
                            
                            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                                // Image Preview Region
                                ElevatedCard(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .heightIn(max = 200.dp),
                                    colors = CardDefaults.elevatedCardColors(
                                        containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
                                    )
                                ) {
                                    Box(
                                        modifier = Modifier.fillMaxSize(),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        val bitmap = rememberImageBitmapFromUri(selectedImage!!.uri)
                                        val crop = resizeConfig.cropRect
                                        
                                        // Calculate base dimensions (considering crop)
                                        val baseWidth = if (crop != null) {
                                            (selectedImage!!.width * (crop.right - crop.left)).toInt()
                                        } else {
                                            selectedImage!!.width
                                        }
                                        val baseHeight = if (crop != null) {
                                            (selectedImage!!.height * (crop.bottom - crop.top)).toInt()
                                        } else {
                                            selectedImage!!.height
                                        }

                                        // Calculate target dimensions
                                        val targetW = resizeConfig.targetWidth ?: baseWidth
                                        val targetH = resizeConfig.targetHeight ?: 
                                            if (resizeConfig.maintainAspectRatio && resizeConfig.targetWidth != null) {
                                                (baseHeight * (resizeConfig.targetWidth!!.toFloat() / baseWidth)).toInt()
                                            } else {
                                                baseHeight
                                            }
                                        
                                        if (bitmap != null) {
                                            // BACKGROUND: Cropped & Dimmed Image
                                            Canvas(modifier = Modifier.fillMaxSize().alpha(0.2f)) {
                                                val bmpW = bitmap.width
                                                val bmpH = bitmap.height
                                                
                                                val srcLeft = (crop?.left ?: 0f) * bmpW
                                                val srcTop = (crop?.top ?: 0f) * bmpH
                                                val srcRight = (crop?.right ?: 1f) * bmpW
                                                val srcBottom = (crop?.bottom ?: 1f) * bmpH
                                                
                                                val srcRect = androidx.compose.ui.unit.IntOffset(srcLeft.toInt(), srcTop.toInt())
                                                val srcSize = androidx.compose.ui.unit.IntSize((srcRight - srcLeft).toInt(), (srcBottom - srcTop).toInt())
                                                
                                                drawImage(
                                                    image = bitmap,
                                                    srcOffset = srcRect,
                                                    srcSize = srcSize,
                                                    dstSize = androidx.compose.ui.unit.IntSize(size.width.toInt(), size.height.toInt())
                                                )
                                            }
                                            
                                            // FOREGROUND: Fitted Image with Target Aspect Ratio
                                            val ratio = if (targetH > 0) targetW.toFloat() / targetH.toFloat() else 1f
                                            
                                            Canvas(
                                                 modifier = Modifier
                                                    .fillMaxSize()
                                                    .padding(8.dp)
                                                    .aspectRatio(ratio)
                                            ) {
                                                val bmpW = bitmap.width
                                                val bmpH = bitmap.height
                                                
                                                val srcLeft = (crop?.left ?: 0f) * bmpW
                                                val srcTop = (crop?.top ?: 0f) * bmpH
                                                val srcRight = (crop?.right ?: 1f) * bmpW
                                                val srcBottom = (crop?.bottom ?: 1f) * bmpH
                                                
                                                val srcRect = androidx.compose.ui.unit.IntOffset(srcLeft.toInt(), srcTop.toInt())
                                                val srcSize = androidx.compose.ui.unit.IntSize((srcRight - srcLeft).toInt(), (srcBottom - srcTop).toInt())
                                                
                                                drawImage(
                                                    image = bitmap,
                                                    srcOffset = srcRect,
                                                    srcSize = srcSize,
                                                    dstSize = androidx.compose.ui.unit.IntSize(size.width.toInt(), size.height.toInt())
                                                )
                                            }

                                        } else {
                                            Icon(Icons.Filled.BrokenImage, null)
                                        }
                                        
                                        // Resolution tag
                                        Surface(
                                            modifier = Modifier
                                                .align(Alignment.BottomEnd)
                                                .padding(8.dp),
                                            shape = MaterialTheme.shapes.extraSmall,
                                            color = MaterialTheme.colorScheme.surface.copy(alpha = 0.9f),
                                            shadowElevation = 2.dp
                                        ) {
                                            Row(
                                                verticalAlignment = Alignment.CenterVertically,
                                                modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                                            ) {
                                                if (targetW != selectedImage!!.width || targetH != selectedImage!!.height) {
                                                    Text(
                                                        "${selectedImage!!.width}x${selectedImage!!.height} → ",
                                                        style = MaterialTheme.typography.labelSmall,
                                                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f) 
                                                    )
                                                    Text(
                                                        "$targetW x $targetH",
                                                        style = MaterialTheme.typography.labelSmall,
                                                        fontWeight = FontWeight.Bold,
                                                        color = MaterialTheme.colorScheme.primary
                                                    )
                                                } else {
                                                     Text(
                                                        "$targetW x $targetH",
                                                        style = MaterialTheme.typography.labelSmall
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }

                                // Quick Presets
                                Text(
                                    stringResource(StudyStrings.ResizeHeaderPresets),
                                    style = MaterialTheme.typography.labelLarge,
                                    fontWeight = FontWeight.Bold
                                )
                                
                                FlowRow(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                                    verticalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    // Maintain Crop Toggle (visible if we have a saved crop)
                                    if (savedCropRect != null) {
                                        FilterChip(
                                            selected = resizeConfig.cropRect != null,
                                            onClick = {
                                                resizeConfig = if (resizeConfig.cropRect != null) {
                                                    // Toggle OFF
                                                    resizeConfig.copy(cropRect = null)
                                                } else {
                                                    // Toggle ON
                                                    resizeConfig.copy(cropRect = savedCropRect)
                                                }
                                            },
                                            label = { Text(stringResource(StudyStrings.ResizeChipCrop)) },
                                            leadingIcon = {
                                                Icon(
                                                    Icons.Filled.Crop,
                                                    null,
                                                    modifier = Modifier.size(18.dp)
                                                )
                                            },
                                            colors = FilterChipDefaults.filterChipColors(
                                                selectedContainerColor = MaterialTheme.colorScheme.tertiaryContainer,
                                                selectedLabelColor = MaterialTheme.colorScheme.onTertiaryContainer
                                            )
                                        )
                                    }

                                    // Keep Ratio Toggle (Moved here)
                                    FilterChip(
                                        selected = resizeConfig.maintainAspectRatio,
                                        onClick = {
                                            resizeConfig = resizeConfig.copy(maintainAspectRatio = !resizeConfig.maintainAspectRatio)
                                        },
                                        label = { Text(stringResource(StudyStrings.ResizeChipRatio)) },
                                        leadingIcon = {
                                            Icon(
                                                if (resizeConfig.maintainAspectRatio) Icons.Filled.CheckCircle else Icons.Outlined.AspectRatio,
                                                null,
                                                modifier = Modifier.size(18.dp)
                                            )
                                        },
                                        colors = FilterChipDefaults.filterChipColors(
                                            selectedContainerColor = MaterialTheme.colorScheme.primaryContainer,
                                            selectedLabelColor = MaterialTheme.colorScheme.onPrimaryContainer
                                        )
                                    )
                                    ResizePreset.entries.forEach { preset ->
                                        FilterChip(
                                            selected = selectedPreset == preset,
                                            onClick = {
                                                selectedPreset = preset
                                                val presetConfig = when (preset) {
                                                    ResizePreset.SIGNATURE -> ImageResizeConfig(targetSizeKB = 20, targetWidth = 400)
                                                    ResizePreset.PASSPORT -> ImageResizeConfig(targetSizeKB = 50, targetWidth = 600)
                                                    ResizePreset.PROFILE -> ImageResizeConfig(targetSizeKB = 100, targetWidth = 800)
                                                    ResizePreset.THUMBNAIL -> ImageResizeConfig(targetSizeKB = 30, targetWidth = 300)
                                                    ResizePreset.DOCUMENT -> ImageResizeConfig(targetSizeKB = 200, targetWidth = 1200)
                                                    ResizePreset.CUSTOM -> resizeConfig
                                                }
                                                // Preserve existing cropRect when applying preset
                                                resizeConfig = presetConfig.copy(cropRect = resizeConfig.cropRect)
                                            },
                                            label = { Text(preset.displayName) }
                                        )
                                    }
                                }
                                
                                // Helper Tip
                                AnimatedVisibility(visible = !resizeConfig.maintainAspectRatio) {
                                    Text(
                                        stringResource(StudyStrings.ResizeTipRatio),
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.secondary,
                                        modifier = Modifier.padding(top = 4.dp)
                                    )
                                }
                                
                                HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
                                
                                // Custom settings
                                Text(
                                    stringResource(StudyStrings.ResizeHeaderCustom),
                                    style = MaterialTheme.typography.labelLarge,
                                    fontWeight = FontWeight.Bold
                                )
                                
                                OutlinedTextField(
                                    value = resizeConfig.targetSizeKB?.toString() ?: "",
                                    onValueChange = {
                                        resizeConfig = resizeConfig.copy(targetSizeKB = it.toIntOrNull())
                                    },
                                    label = { Text(stringResource(StudyStrings.ResizeLabelTargetSize)) },
                                    leadingIcon = { Icon(Icons.Outlined.Storage, null) },
                                    placeholder = { Text(stringResource(StudyStrings.ResizePlaceholderKb)) },
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true
                                )
                                
                                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    OutlinedTextField(
                                        value = resizeConfig.targetWidth?.toString() ?: "",
                                        onValueChange = {
                                            resizeConfig = resizeConfig.copy(targetWidth = it.toIntOrNull())
                                        },
                                        label = { Text(stringResource(StudyStrings.ResizeLabelWidth)) },
                                        placeholder = { Text(stringResource(StudyStrings.ResizePlaceholderPx)) },
                                        modifier = Modifier.weight(1f),
                                        singleLine = true
                                    )
                                    
                                    OutlinedTextField(
                                        value = resizeConfig.targetHeight?.toString() ?: "",
                                        onValueChange = {
                                            resizeConfig = resizeConfig.copy(targetHeight = it.toIntOrNull())
                                        },
                                        label = { Text(stringResource(StudyStrings.ResizeLabelHeight)) },
                                        placeholder = { Text(stringResource(StudyStrings.ResizePlaceholderPx)) },
                                        modifier = Modifier.weight(1f),
                                        singleLine = true
                                    )
                                }
                                
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(stringResource(StudyStrings.ResizeLabelQuality, resizeConfig.quality))
                                }
                                
                                Slider(
                                    value = resizeConfig.quality.toFloat(),
                                    onValueChange = {
                                        resizeConfig = resizeConfig.copy(quality = it.toInt())
                                    },
                                    valueRange = 10f..100f,
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }
                        }
                    }
                    
                    // STEP 3: Resize Button
                    AnimatedVisibility(visible = selectedImage != null && result == null) {
                        Button(
                            onClick = {
                                isResizing = true
                                errorMessage = null
                                scope.launch {
                                    try {
                                        val imageToResize = selectedImage!!
                                        val resizeResult = resizerService.resizeImage(imageToResize, resizeConfig)
                                        result = resizeResult
                                        
                                        
                                        if (resizeResult.success) {
                                            successMessage = strSuccessMessage
                                        } else {
                                            errorMessage = resizeResult.error
                                        }
                                    } catch (e: Exception) {
                                        errorMessage = "$strErrorStart ${e.message ?: ""}"
                                    } finally {
                                        isResizing = false
                                    }
                                }
                            },
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(56.dp),
                            enabled = !isResizing
                        ) {
                            if (isResizing) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(24.dp),
                                    color = MaterialTheme.colorScheme.onPrimary
                                )
                                Spacer(Modifier.width(12.dp))
                                Text(stringResource(StudyStrings.ResizeStatusProcessing))
                            } else {
                                Icon(Icons.Filled.AutoFixHigh, null)
                                Spacer(Modifier.width(8.dp))
                                Text(stringResource(StudyStrings.ResizeStep3Btn), style = MaterialTheme.typography.titleMedium)
                            }
                        }
                    }
                    
                    // STEP 4: View Result
                    result?.let { res ->
                        if (res.success) {
                            AnimatedVisibility(
                                visible = true,
                                enter = slideInVertically(initialOffsetY = { it }) + fadeIn()
                            ) {
                                StepCard(
                                    stepNumber = 3,
                                    title = stringResource(StudyStrings.ResizeStep4Title),
                                    subtitle = stringResource(StudyStrings.ResizeStep4Subtitle),
                                    isCompleted = true,
                                    isActive = true,
                                    icon = Icons.Filled.CheckCircle
                                ) {
                                    // Result Image Preview with Hold-to-Compare
                                    res.resizedUri?.let { uri ->
                                        var isHoldingOriginal by remember { mutableStateOf(false) }
                                        
                                        ElevatedCard(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .height(280.dp) // Taller for better view
                                                .padding(bottom = 16.dp),
                                            colors = CardDefaults.elevatedCardColors(
                                                containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)
                                            )
                                        ) {
                                            Box(
                                                modifier = Modifier
                                                    .fillMaxSize()
                                                    .pointerInput(Unit) {
                                                        detectTapGestures(
                                                            onPress = {
                                                                isHoldingOriginal = true
                                                                tryAwaitRelease()
                                                                isHoldingOriginal = false
                                                            }
                                                        )
                                                    },
                                                contentAlignment = Alignment.Center
                                            ) {
                                                // Dynamic Image Source
                                                val targetUri = if (isHoldingOriginal) selectedImage!!.uri else uri
                                                val bitmap = rememberImageBitmapFromUri(targetUri)
                                                
                                                if (bitmap != null) {
                                                    // BACKGROUND: Cropped & Dimmed Image
                                                    Image(
                                                        bitmap = bitmap,
                                                        contentDescription = null,
                                                        contentScale = ContentScale.Crop,
                                                        modifier = Modifier.fillMaxSize(),
                                                        alpha = 0.15f
                                                    )
                                                    
                                                    // FOREGROUND: Fitted Image
                                                    Image(
                                                        bitmap = bitmap,
                                                        contentDescription = "Resized Image",
                                                        contentScale = ContentScale.Fit,
                                                        modifier = Modifier
                                                            .fillMaxSize()
                                                            .padding(4.dp)
                                                    )
                                                } else {
                                                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                                        Icon(Icons.Filled.Image, null, modifier = Modifier.size(48.dp))
                                                        Text(stringResource(StudyStrings.ResizePreviewUnavailable))
                                                    }
                                                }
                                                
                                                // Status Badge (Top-Left)
                                                Surface(
                                                    modifier = Modifier
                                                        .align(Alignment.TopStart)
                                                        .padding(12.dp),
                                                    shape = MaterialTheme.shapes.small,
                                                    color = if (isHoldingOriginal) 
                                                        MaterialTheme.colorScheme.secondaryContainer 
                                                    else MaterialTheme.colorScheme.primaryContainer,
                                                    shadowElevation = 4.dp
                                                ) {
                                                    Row(
                                                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
                                                        verticalAlignment = Alignment.CenterVertically
                                                    ) {
                                                        Icon(
                                                            if (isHoldingOriginal) Icons.Outlined.History else Icons.Filled.CheckCircle,
                                                            null,
                                                            modifier = Modifier.size(14.dp)
                                                        )
                                                        Spacer(Modifier.width(4.dp))
                                                        Text(
                                                            if (isHoldingOriginal) stringResource(StudyStrings.ResizePreviewOriginal) else stringResource(StudyStrings.ResizePreviewResult),
                                                            style = MaterialTheme.typography.labelMedium,
                                                            fontWeight = FontWeight.Bold
                                                        )
                                                    }
                                                }
                                                
                                                // Comparison Hint (Bottom-Center) - Hides when holding
                                                androidx.compose.animation.AnimatedVisibility(
                                                    visible = !isHoldingOriginal,
                                                    modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = 12.dp),
                                                    enter = fadeIn(),
                                                    exit = fadeOut()
                                                ) {
                                                    Surface(
                                                        shape = CircleShape,
                                                        color = Color.Black.copy(alpha = 0.6f)
                                                    ) {
                                                        Text(
                                                            "Hold to Compare",
                                                            color = Color.White,
                                                            style = MaterialTheme.typography.labelSmall,
                                                            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp)
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                        
                                        // Detailed Reduction Info
                                        val savedBytes = selectedImage!!.sizeInBytes - res.resizedSizeInBytes
                                        val savedPercent = ((1 - res.compressionRatio) * 100).toInt()
                                        
                                        if (savedPercent > 0) {
                                            Card(
                                                modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp),
                                                colors = CardDefaults.cardColors(
                                                    containerColor = Color(0xFFE8F5E9) // Light Green
                                                )
                                            ) {
                                                Row(
                                                    modifier = Modifier.padding(12.dp).fillMaxWidth(),
                                                    verticalAlignment = Alignment.CenterVertically,
                                                    horizontalArrangement = Arrangement.Center
                                                ) {
                                                    Icon(
                                                        Icons.AutoMirrored.Outlined.TrendingDown, 
                                                        null, 
                                                        tint = Color(0xFF2E7D32) // Dark Green
                                                    )
                                                    Spacer(Modifier.width(8.dp))
                                                    Text(
                                                        "Reduced by $savedPercent%",
                                                        style = MaterialTheme.typography.titleMedium,
                                                        fontWeight = FontWeight.Bold,
                                                        color = Color(0xFF2E7D32)
                                                    )
                                                    Text(
                                                        " (${(savedBytes / 1024.0).toInt()} KB saved)",
                                                        style = MaterialTheme.typography.bodyMedium,
                                                        color = Color(0xFF2E7D32)
                                                    )
                                                }
                                            }
                                        }
                                    }

                                    // Stats
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                                    ) {
                                        OutlinedCard(modifier = Modifier.weight(1f)) {
                                            Column(
                                                modifier = Modifier.padding(16.dp),
                                                horizontalAlignment = Alignment.CenterHorizontally
                                            ) {
                                                Icon(Icons.Outlined.Storage, null, tint = MaterialTheme.colorScheme.primary)
                                                Spacer(Modifier.height(4.dp))
                                                Text(
                                                    "${(res.resizedSizeInBytes / 1024.0 * 10).toInt() / 10.0} KB",
                                                    style = MaterialTheme.typography.titleLarge,
                                                    fontWeight = FontWeight.Bold
                                                )
                                                Text("New Size", style = MaterialTheme.typography.bodySmall)
                                            }
                                        }
                                        
                                        OutlinedCard(modifier = Modifier.weight(1f)) {
                                            Column(
                                                modifier = Modifier.padding(16.dp),
                                                horizontalAlignment = Alignment.CenterHorizontally
                                            ) {
                                                Icon(Icons.AutoMirrored.Outlined.TrendingDown, null, tint = MaterialTheme.colorScheme.tertiary)
                                                Spacer(Modifier.height(4.dp))
                                                val saved = ((1 - res.compressionRatio) * 100).toInt()
                                                Text(
                                                    "$saved%",
                                                    style = MaterialTheme.typography.titleLarge,
                                                    fontWeight = FontWeight.Bold,
                                                    color = MaterialTheme.colorScheme.tertiary
                                                )
                                                Text("Saved", style = MaterialTheme.typography.bodySmall)
                                            }
                                        }
                                    }
                                    
                                    Spacer(Modifier.height(16.dp))
                                    
                                    Text(
                                        "${res.resizedWidth}×${res.resizedHeight} px",
                                        style = MaterialTheme.typography.titleMedium,
                                        modifier = Modifier.fillMaxWidth(),
                                        textAlign = androidx.compose.ui.text.style.TextAlign.Center
                                    )
                                    
                                    Spacer(Modifier.height(16.dp))
                                    
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                                    ) {
                                        FilledTonalButton(
                                            onClick = {
                                                scope.launch {
                                                    try {
                                                        res.resizedUri?.let { uri ->
                                                            PlatformFileHelper.openFile(uri, "image/jpeg")
                                                        }
                                                    } catch (e: Exception) {
                                                        errorMessage = "Failed to open"
                                                    }
                                                }
                                            },
                                            modifier = Modifier.weight(1f),
                                            enabled = res.resizedUri != null
                                        ) {
                                            Icon(Icons.AutoMirrored.Outlined.OpenInNew, null)
                                            Spacer(Modifier.width(8.dp))
                                            Text("Open")
                                        }
                                        
                                        FilledTonalButton(
                                            onClick = {
                                                scope.launch {
                                                    try {
                                                        res.resizedUri?.let { uri ->
                                                            PlatformFileHelper.shareFile(uri, "image/jpeg")
                                                        }
                                                    } catch (e: Exception) {
                                                        errorMessage = "Failed to share"
                                                    }
                                                }
                                            },
                                            modifier = Modifier.weight(1f),
                                            enabled = res.resizedUri != null
                                        ) {
                                            Icon(Icons.Outlined.Share, null)
                                            Spacer(Modifier.width(8.dp))
                                            Text("Share")
                                        }
                                    }
                                    
                                    Spacer(Modifier.height(12.dp))
                                    
                                    OutlinedButton(
                                        onClick = {
                                            selectedImage = null
                                            result = null
                                            resizeConfig = ImageResizeConfig()
                                        },
                                        modifier = Modifier.fillMaxWidth()
                                    ) {
                                        Icon(Icons.Outlined.Refresh, null)
                                        Spacer(Modifier.width(8.dp))
                                        Text("Resize Another Image")
                                    }
                                }
                            }
                        }
                    }
                } // Close scrollable column
            }
        }
        } // Close ResponsiveContent
    }
    
    if (showPresetDialog) {
        PresetDialog(
            currentPreset = selectedPreset,
            onDismiss = { showPresetDialog = false },
            onSelect = { preset ->
                selectedPreset = preset
                // Apply preset to config
                val presetConfig = when (preset) {
                    ResizePreset.SIGNATURE -> ImageResizeConfig(targetSizeKB = 20, targetWidth = 400)
                    ResizePreset.PASSPORT -> ImageResizeConfig(targetSizeKB = 50, targetWidth = 600)
                    ResizePreset.PROFILE -> ImageResizeConfig(targetSizeKB = 100, targetWidth = 800)
                    ResizePreset.THUMBNAIL -> ImageResizeConfig(targetSizeKB = 30, targetWidth = 300)
                    ResizePreset.DOCUMENT -> ImageResizeConfig(targetSizeKB = 200, targetWidth = 1200)
                    ResizePreset.CUSTOM -> resizeConfig
                }
                resizeConfig = presetConfig
                showPresetDialog = false
            }
        )
    }
    
    if (showCropDialog && selectedImage != null) {
        CropDialog(
            imageUri = selectedImage!!.uri,
            initialCrop = resizeConfig.cropRect,
            onDismiss = { showCropDialog = false },
            onConfirm = { rect ->
                savedCropRect = rect
                resizeConfig = resizeConfig.copy(cropRect = rect)
                showCropDialog = false
            }
        )
    }
}

@Composable
fun PresetDialog(
    currentPreset: ResizePreset,
    onDismiss: () -> Unit,
    onSelect: (ResizePreset) -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Select Preset") },
        text = {
            Column {
                ResizePreset.entries.forEach { preset ->
                    Card(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(vertical = 4.dp),
                        onClick = { onSelect(preset) }
                    ) {
                        Column(modifier = Modifier.padding(12.dp)) {
                            Text(
                                preset.displayName,
                                style = MaterialTheme.typography.titleSmall
                            )
                            Text(
                                preset.description,
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        }
    )
}

@Composable
fun StepCard(
    stepNumber: Int,
    title: String,
    subtitle: String,
    isCompleted: Boolean,
    isActive: Boolean,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    content: @Composable ColumnScope.() -> Unit
) {
    ElevatedCard(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.elevatedCardColors(
            containerColor = if (isActive) 
                MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.3f)
            else MaterialTheme.colorScheme.surface
        )
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                // Step number badge
                Surface(
                    modifier = Modifier.size(48.dp),
                    shape = CircleShape,
                    color = when {
                        isCompleted -> MaterialTheme.colorScheme.primary
                        isActive -> MaterialTheme.colorScheme.primaryContainer
                        else -> MaterialTheme.colorScheme.surfaceVariant
                    }
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        if (isCompleted) {
                            Icon(
                                Icons.Filled.CheckCircle,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.onPrimary
                            )
                        } else {
                            Icon(
                                icon,
                                contentDescription = null,
                                tint = if (isActive) 
                                    MaterialTheme.colorScheme.onPrimaryContainer 
                                else MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
                
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        "Step $stepNumber",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.primary,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        title,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        subtitle,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
            
            if (isActive || isCompleted) {
                Spacer(Modifier.height(16.dp))
                content()
            }
        }
    }

}

@Composable
fun CropDialog(
    imageUri: String,
    initialCrop: CropRect?,
    onDismiss: () -> Unit,
    onConfirm: (CropRect?) -> Unit
) {
    var crop by remember { 
        mutableStateOf(initialCrop ?: CropRect(0f, 0f, 1f, 1f)) 
    }
    
    // Track which handle is being dragged: 0=None, 1=TopLeft, 2=TopRight, 3=BottomLeft, 4=BottomRight, 5=Center
    var activeHandle by remember { mutableStateOf(0) }
    
    val density = androidx.compose.ui.platform.LocalDensity.current
    
    Dialog(onDismissRequest = onDismiss) {
        Card(
            modifier = Modifier
                .fillMaxWidth()
                .fillMaxHeight(0.9f)
                .padding(16.dp),
            colors = CardDefaults.cardColors(
                containerColor = MaterialTheme.colorScheme.surface
            )
        ) {
            Column(
                modifier = Modifier.padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Text(
                    "Crop Image",
                    style = MaterialTheme.typography.titleLarge
                )
                
                BoxWithConstraints(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxWidth()
                        .background(Color.Black),
                    contentAlignment = Alignment.Center
                ) {
                    val bitmap = rememberImageBitmapFromUri(imageUri)
                    
                    if (bitmap != null) {
                        // Calculate displayed image bounds
                        val imageInfos = remember(maxWidth, maxHeight, bitmap) {
                            val imgW = bitmap.width.toFloat()
                            val imgH = bitmap.height.toFloat()
                            val containerW = maxWidth.value
                            val containerH = maxHeight.value
                            val density = 1f // Approximate, we work in relative coordinates mostly? 
                            // Actually maxWidth.value is in dp, we need pixels for accurate calc if we used Canvas size
                            // But here we can just rely on aspect ratios for normalized coords
                            
                            val imgAspect = imgW / imgH
                            val containerAspect = containerW / containerH
                            
                            // "Fit" logic
                            if (imgAspect > containerAspect) {
                                // Width constrained
                                val displayW = containerW
                                val displayH = containerW / imgAspect
                                Triple(displayW, displayH, imgAspect)
                            } else {
                                // Height constrained
                                val displayW = containerH * imgAspect
                                val displayH = containerH
                                Triple(displayW, displayH, imgAspect)
                            }
                        }
                        
                        // Render Image
                        Image(
                            bitmap = bitmap,
                            contentDescription = null,
                            modifier = Modifier.fillMaxSize(),
                            contentScale = ContentScale.Fit
                        )
                        
                        // Overlay & Gestures
                        androidx.compose.foundation.Canvas(
                            modifier = Modifier
                                .fillMaxSize()
                                .pointerInput(imageInfos) {
                                    detectDragGestures(
                                        onDragStart = { offset ->
                                            // Determine active handle based on touch position relative to crop rect
                                            val (w, h) = size
                                            // We need to map screen offset to normalized crop coords
                                            // BUT we first need to know where the image ACTUALLY is within this canvas (assuming FillMaxSize canvas over Fit image)
                              
                                            // Re-calculate image bounds in pixels
                                            val imgRatio = bitmap.width.toFloat() / bitmap.height.toFloat()
                                            val canvasRatio = w.toFloat() / h.toFloat()
                                            
                                            val (drawW, drawH) = if (imgRatio > canvasRatio) {
                                                Pair(w.toFloat(), w.toFloat() / imgRatio)
                                            } else {
                                                Pair(h.toFloat() * imgRatio, h.toFloat())
                                            }
                                            
                                            val offsetX = (w - drawW) / 2f
                                            val offsetY = (h - drawH) / 2f
                                            
                                            // Localize touch to image
                                            val localX = (offset.x - offsetX) / drawW
                                            val localY = (offset.y - offsetY) / drawH
                                            
                                            // Handle radius of 48dp for touch target
                                            val touchRadiusPx = with(density) { 48.dp.toPx() / 2f }
                                            
                                            // Convert touch radius to normalized coordinates relative to image
                                            val hitX = touchRadiusPx / drawW
                                            val hitY = touchRadiusPx / drawH
                                            
                                            activeHandle = when {
                                                // Corners
                                                kotlin.math.abs(localX - crop.left) < hitX && kotlin.math.abs(localY - crop.top) < hitY -> 1 // TL
                                                kotlin.math.abs(localX - crop.right) < hitX && kotlin.math.abs(localY - crop.top) < hitY -> 2 // TR
                                                kotlin.math.abs(localX - crop.left) < hitX && kotlin.math.abs(localY - crop.bottom) < hitY -> 3 // BL
                                                kotlin.math.abs(localX - crop.right) < hitX && kotlin.math.abs(localY - crop.bottom) < hitY -> 4 // BR
                                                
                                                // Center/Pan
                                                localX in crop.left..crop.right && localY in crop.top..crop.bottom -> 5
                                                else -> 0
                                            }
                                        },
                                        onDrag = { _, dragAmount ->
                                            if (activeHandle == 0) return@detectDragGestures
                                            
                                            val (w, h) = size
                                             // Re-calculate image bounds (duplicated logic, should optimize but fine for now)
                                            val imgRatio = bitmap.width.toFloat() / bitmap.height.toFloat()
                                            val canvasRatio = w.toFloat() / h.toFloat()
                                            val (drawW, drawH) = if (imgRatio > canvasRatio) {
                                                Pair(w.toFloat(), w.toFloat() / imgRatio)
                                            } else {
                                                Pair(h.toFloat() * imgRatio, h.toFloat())
                                            }
                                            
                                            val dx = dragAmount.x / drawW
                                            val dy = dragAmount.y / drawH
                                            
                                            var newLeft = crop.left
                                            var newTop = crop.top
                                            var newRight = crop.right
                                            var newBottom = crop.bottom
                                            
                                            when (activeHandle) {
                                                1 -> { // TL
                                                    newLeft = (newLeft + dx).coerceIn(0f, newRight - 0.1f)
                                                    newTop = (newTop + dy).coerceIn(0f, newBottom - 0.1f)
                                                }
                                                2 -> { // TR
                                                    newRight = (newRight + dx).coerceIn(newLeft + 0.1f, 1f)
                                                    newTop = (newTop + dy).coerceIn(0f, newBottom - 0.1f)
                                                }
                                                3 -> { // BL
                                                    newLeft = (newLeft + dx).coerceIn(0f, newRight - 0.1f)
                                                    newBottom = (newBottom + dy).coerceIn(newTop + 0.1f, 1f)
                                                }
                                                4 -> { // BR
                                                    newRight = (newRight + dx).coerceIn(newLeft + 0.1f, 1f)
                                                    newBottom = (newBottom + dy).coerceIn(newTop + 0.1f, 1f)
                                                }
                                                5 -> { // Pan
                                                    val width = newRight - newLeft
                                                    val height = newBottom - newTop
                                                    
                                                    // Try to move X
                                                    if (dx > 0) {
                                                        if (newRight + dx <= 1f) {
                                                            newLeft += dx
                                                            newRight += dx
                                                        }
                                                    } else {
                                                        if (newLeft + dx >= 0f) {
                                                            newLeft += dx
                                                            newRight += dx
                                                        }
                                                    }
                                                    
                                                    // Try to move Y
                                                    if (dy > 0) {
                                                        if (newBottom + dy <= 1f) {
                                                            newTop += dy
                                                            newBottom += dy
                                                        }
                                                    } else {
                                                        if (newTop + dy >= 0f) {
                                                            newTop += dy
                                                            newBottom += dy
                                                        }
                                                    }
                                                }
                                            }
                                            
                                            crop = CropRect(newLeft, newTop, newRight, newBottom)
                                        },
                                        onDragEnd = { activeHandle = 0 },
                                        onDragCancel = { activeHandle = 0 }
                                    )
                                }
                        ) {
                            val w = size.width
                            val h = size.height
                            
                            val imgRatio = bitmap.width.toFloat() / bitmap.height.toFloat()
                            val canvasRatio = w / h
                            
                            val (drawW, drawH) = if (imgRatio > canvasRatio) {
                                Pair(w, w / imgRatio)
                            } else {
                                Pair(h * imgRatio, h)
                            }
                            
                            val offsetX = (w - drawW) / 2f
                            val offsetY = (h - drawH) / 2f
                            
                            // Map normalized crop to pixels
                            val cropLeftPx = offsetX + (crop.left * drawW)
                            val cropTopPx = offsetY + (crop.top * drawH)
                            val cropRightPx = offsetX + (crop.right * drawW)
                            val cropBottomPx = offsetY + (crop.bottom * drawH)
                            
                            // Draw Semi-transparent overlay (4 rects around the crop)
                            val overlayColor = Color.Black.copy(alpha = 0.6f)
                            
                            // Top rect (Full width)
                            drawRect(
                                color = overlayColor,
                                topLeft = androidx.compose.ui.geometry.Offset(0f, 0f),
                                size = androidx.compose.ui.geometry.Size(w, cropTopPx)
                            )
                            // Bottom rect (Full width)
                            drawRect(
                                color = overlayColor,
                                topLeft = androidx.compose.ui.geometry.Offset(0f, cropBottomPx),
                                size = androidx.compose.ui.geometry.Size(w, h - cropBottomPx)
                            )
                            // Left rect (Between top and bottom)
                            drawRect(
                                color = overlayColor,
                                topLeft = androidx.compose.ui.geometry.Offset(0f, cropTopPx),
                                size = androidx.compose.ui.geometry.Size(cropLeftPx, cropBottomPx - cropTopPx)
                            )
                            // Right rect (Between top and bottom)
                            drawRect(
                                color = overlayColor,
                                topLeft = androidx.compose.ui.geometry.Offset(cropRightPx, cropTopPx),
                                size = androidx.compose.ui.geometry.Size(w - cropRightPx, cropBottomPx - cropTopPx)
                            )
                            
                            // Draw Border
                            drawRect(
                                color = Color.White,
                                topLeft = androidx.compose.ui.geometry.Offset(cropLeftPx, cropTopPx),
                                size = androidx.compose.ui.geometry.Size(cropRightPx - cropLeftPx, cropBottomPx - cropTopPx),
                                style = androidx.compose.ui.graphics.drawscope.Stroke(width = 3f)
                            )
                            
                            // Draw Corner Handles
                            val handleSize = 20f
                            val handleColor = Color.White
                            
                            // TL
                            drawCircle(handleColor, radius = handleSize, center = androidx.compose.ui.geometry.Offset(cropLeftPx, cropTopPx))
                            // TR
                            drawCircle(handleColor, radius = handleSize, center = androidx.compose.ui.geometry.Offset(cropRightPx, cropTopPx))
                            // BL
                            drawCircle(handleColor, radius = handleSize, center = androidx.compose.ui.geometry.Offset(cropLeftPx, cropBottomPx))
                            // BR
                            drawCircle(handleColor, radius = handleSize, center = androidx.compose.ui.geometry.Offset(cropRightPx, cropBottomPx))
                        }
                    }
                }
                
                Text(
                    "Drag corners to crop", 
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.align(Alignment.CenterHorizontally)
                )
                
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.End,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    TextButton(onClick = { onConfirm(null) }) {
                        Text("Reset")
                    }
                    Spacer(Modifier.width(8.dp))
                    TextButton(onClick = onDismiss) {
                        Text("Cancel")
                    }
                    Spacer(Modifier.width(8.dp))
                    Button(onClick = { onConfirm(crop) }) {
                        Text("Apply Crop")
                    }
                }
            }
        }
    }
}
